import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ICompany } from '../companies/models/company.interface';
import { CompanyService } from '../companies/services/company.service';
import { CatalogoProdutosService } from '../catalogo-produtos/services/catalogo-produtos.service';
import {
  EMPTY_SKINCARE_THRESHOLD_SETTINGS,
  ISkincareThresholdSettings,
  IUpsertSkincareThresholdSettingsInput
} from '../catalogo-produtos/models/catalog';
import { SessionService } from '../../shared/services/session.service';
import { ToastService } from '../../shared/services/toast.service';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';

type ThresholdControlName = keyof IUpsertSkincareThresholdSettingsInput;
type ThresholdSectionKey = 'default' | 'company';

interface IThresholdField {
  controlName: ThresholdControlName;
  label: string;
}

interface IClassificationPreviewItem {
  label: string;
  range: string;
  dotClass: string;
}

interface IInlineEditorState {
  section: ThresholdSectionKey;
  controlName: ThresholdControlName;
}

@Component({
  selector: 'app-classificacao-skincare',
  templateUrl: './classificacao-skincare.component.html',
  styleUrls: ['./classificacao-skincare.component.scss']
})
export class ClassificacaoSkincareComponent implements OnInit {
  ERoleAdminString = ERoleAdminString;
  readonly thresholdFields: IThresholdField[] = [
    { controlName: 'veryBadMax', label: 'Cuidados urgentes' },
    { controlName: 'needProcessingMax', label: 'Precisa de cuidados' },
    { controlName: 'canImproveMax', label: 'Pode melhorar' },
    { controlName: 'veryGoodMax', label: 'Muito bom' }
  ];

  readonly absoluteMin = 1;
  readonly absoluteMax = 99;

  defaultThresholdForm = this.createThresholdForm();
  companyThresholdForm = this.createThresholdForm();
  companyControl = new UntypedFormControl(null, [Validators.required]);
  activeInlineEditor: IInlineEditorState | null = null;

  companies: ICompany[] = [];

  isLoadingDefaultThresholds = false;
  isSavingDefaultThresholds = false;
  defaultThresholdsMessage = '';

  isLoadingCompanyThresholds = false;
  isSavingCompanyThresholds = false;
  isResettingCompanyThresholds = false;
  companyThresholdsMessage = '';
  hasCustomThresholds = false;

  constructor(
    private readonly companyService: CompanyService,
    private readonly catalogoProdutosService: CatalogoProdutosService,
    private readonly sessionService: SessionService,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.companyService.listarEmpresasAtivas().subscribe((response) => {
      this.companies = response.data || [];
    });

    const sessionCompanyId = this.sessionService.getSession()?.companyId;
    if (sessionCompanyId) {
      this.companyControl.setValue(sessionCompanyId);
      this.companyControl.disable({ emitEvent: false });
    }

    this.loadDefaultThresholdSettings();

    const initialCompanyId = String(this.companyControl.value || '');
    if (initialCompanyId) {
      this.loadCompanyThresholdSettings(initialCompanyId);
    }
  }

  onCompanySelectionChanged(companyId: string | null): void {
    this.cancelInlineEdit();

    if (!companyId) {
      this.applyThresholdSettings(this.companyThresholdForm, EMPTY_SKINCARE_THRESHOLD_SETTINGS);
      this.companyThresholdsMessage = '';
      this.hasCustomThresholds = false;
      return;
    }

    this.loadCompanyThresholdSettings(String(companyId));
  }

  loadDefaultThresholdSettings(): void {
    this.cancelInlineEdit();
    this.isLoadingDefaultThresholds = true;
    this.defaultThresholdsMessage = '';

    this.catalogoProdutosService.listarThresholdsDefault().subscribe({
      next: (response) => {
        const defaultSettings = response?.data || EMPTY_SKINCARE_THRESHOLD_SETTINGS;

        this.applyThresholdSettings(this.defaultThresholdForm, defaultSettings);
        this.defaultThresholdsMessage = 'Configuração padrão carregada com sucesso.';
        this.isLoadingDefaultThresholds = false;

        if (this.companyControl.value && !this.hasCustomThresholds) {
          this.applyThresholdSettings(this.companyThresholdForm, defaultSettings);
        }
      },
      error: () => {
        this.applyThresholdSettings(this.defaultThresholdForm, EMPTY_SKINCARE_THRESHOLD_SETTINGS);
        this.defaultThresholdsMessage = 'Não foi possível carregar a configuração padrão.';
        this.isLoadingDefaultThresholds = false;
      }
    });
  }

  saveDefaultThresholdSettings(): void {
    if (!this.isThresholdFormValid(this.defaultThresholdForm)) {
      this.toastService.toast(this.getThresholdValidationMessage(this.defaultThresholdForm));
      return;
    }

    this.isSavingDefaultThresholds = true;
    this.catalogoProdutosService
      .salvarThresholdsDefault(this.getThresholdPayload(this.defaultThresholdForm))
      .subscribe({
        next: (response) => {
          const defaultSettings = response?.data || EMPTY_SKINCARE_THRESHOLD_SETTINGS;

          this.applyThresholdSettings(this.defaultThresholdForm, defaultSettings);
          this.defaultThresholdsMessage = 'Configuração padrão salva com sucesso.';
          this.isSavingDefaultThresholds = false;

          if (this.companyControl.value && !this.hasCustomThresholds) {
            this.applyThresholdSettings(this.companyThresholdForm, defaultSettings);
            this.companyThresholdsMessage = 'A empresa selecionada continua usando a configuração padrão.';
          }
        },
        error: () => {
          this.isSavingDefaultThresholds = false;
        }
      });
  }

  loadCompanyThresholdSettings(companyId: string): void {
    if (!companyId) {
      this.applyThresholdSettings(this.companyThresholdForm, EMPTY_SKINCARE_THRESHOLD_SETTINGS);
      this.companyThresholdsMessage = '';
      this.hasCustomThresholds = false;
      return;
    }

    this.cancelInlineEdit();
    this.isLoadingCompanyThresholds = true;
    this.companyThresholdsMessage = '';

    this.catalogoProdutosService.listarThresholdsPorCompanyId(companyId).subscribe({
      next: (response) => {
        const companySettings = response?.data || { ...EMPTY_SKINCARE_THRESHOLD_SETTINGS, companyId };

        this.applyThresholdSettings(this.companyThresholdForm, companySettings);
        this.hasCustomThresholds = Boolean(companySettings.isCustom);
        this.companyThresholdsMessage = companySettings.isCustom
          ? 'Esta empresa usa uma configuração própria.'
          : 'Esta empresa usa o padrão do sistema.';
        this.isLoadingCompanyThresholds = false;
      },
      error: () => {
        this.applyThresholdSettings(this.companyThresholdForm, { ...EMPTY_SKINCARE_THRESHOLD_SETTINGS, companyId });
        this.companyThresholdsMessage = 'Não foi possível carregar a configuração da empresa.';
        this.hasCustomThresholds = false;
        this.isLoadingCompanyThresholds = false;
      }
    });
  }

  saveCompanyThresholdSettings(): void {
    const companyId = String(this.companyControl.value || '');
    if (!companyId) {
      this.toastService.toast('Selecione uma empresa para personalizar a classificação.');
      return;
    }

    if (!this.isThresholdFormValid(this.companyThresholdForm)) {
      this.toastService.toast(this.getThresholdValidationMessage(this.companyThresholdForm));
      return;
    }

    this.isSavingCompanyThresholds = true;
    this.catalogoProdutosService
      .salvarThresholds(companyId, this.getThresholdPayload(this.companyThresholdForm))
      .subscribe({
        next: (response) => {
          const companySettings = response?.data || {
            ...EMPTY_SKINCARE_THRESHOLD_SETTINGS,
            companyId,
            isCustom: true
          };

          this.applyThresholdSettings(this.companyThresholdForm, companySettings);
          this.hasCustomThresholds = true;
          this.companyThresholdsMessage = 'Configuração da empresa salva com sucesso.';
          this.isSavingCompanyThresholds = false;
        },
        error: () => {
          this.isSavingCompanyThresholds = false;
        }
      });
  }

  resetCompanyThresholdSettings(): void {
    const companyId = String(this.companyControl.value || '');
    if (!companyId) {
      this.toastService.toast('Selecione uma empresa para voltar ao padrão do sistema.');
      return;
    }

    this.isResettingCompanyThresholds = true;
    this.catalogoProdutosService.resetarThresholds(companyId).subscribe({
      next: (response) => {
        const companySettings = response?.data || { ...EMPTY_SKINCARE_THRESHOLD_SETTINGS, companyId };

        this.applyThresholdSettings(this.companyThresholdForm, companySettings);
        this.hasCustomThresholds = false;
        this.companyThresholdsMessage = 'A empresa voltou a usar o padrão do sistema.';
        this.isResettingCompanyThresholds = false;
      },
      error: () => {
        this.isResettingCompanyThresholds = false;
      }
    });
  }

  copyDefaultToCompanyForm(): void {
    if (!this.isThresholdFormValid(this.defaultThresholdForm)) {
      this.toastService.toast('Carregue uma configuração padrão válida antes de copiar os valores.');
      return;
    }

    this.applyThresholdSettings(this.companyThresholdForm, {
      ...EMPTY_SKINCARE_THRESHOLD_SETTINGS,
      ...this.getThresholdPayload(this.defaultThresholdForm)
    });
    this.companyThresholdsMessage = 'Valores do padrão copiados para a empresa.';
  }

  startInlineEdit(section: ThresholdSectionKey, controlName: ThresholdControlName): void {
    this.activeInlineEditor = { section, controlName };

    window.setTimeout(() => {
      const input = document.getElementById(this.getInlineInputId(section, controlName)) as HTMLInputElement | null;
      input?.focus();
      input?.select();
    });
  }

  isInlineEditing(section: ThresholdSectionKey, controlName: ThresholdControlName): boolean {
    return this.activeInlineEditor?.section === section && this.activeInlineEditor?.controlName === controlName;
  }

  commitInlineValue(
    section: ThresholdSectionKey,
    controlName: ThresholdControlName,
    rawValue: string | number | null
  ): void {
    const form = this.getThresholdForm(section);
    const min = this.getRangeMin(form, controlName);
    const max = this.getRangeMax(form, controlName);
    const currentValue = this.getControlValue(form, controlName) ?? min;
    const parsedValue = Number(rawValue);
    const nextValue = Number.isFinite(parsedValue) ? Math.round(parsedValue) : currentValue;
    const normalizedValue = Math.min(max, Math.max(min, nextValue));

    form.get(controlName)?.setValue(normalizedValue);
    this.cancelInlineEdit();
  }

  cancelInlineEdit(): void {
    this.activeInlineEditor = null;
  }

  getControlValue(form: UntypedFormGroup, controlName: ThresholdControlName): number | null {
    const value = form.get(controlName)?.value;

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  getRangeMin(form: UntypedFormGroup, controlName: ThresholdControlName): number {
    const previousControl = this.getPreviousControlName(controlName);
    const previousValue = previousControl ? this.getControlValue(form, previousControl) : null;
    const minValue = previousValue !== null ? previousValue + 1 : this.absoluteMin;

    return Math.min(this.absoluteMax, Math.max(this.absoluteMin, minValue));
  }

  getRangeMax(form: UntypedFormGroup, controlName: ThresholdControlName): number {
    const nextControl = this.getNextControlName(controlName);
    const nextValue = nextControl ? this.getControlValue(form, nextControl) : null;
    const minValue = this.getRangeMin(form, controlName);
    const maxValue = nextValue !== null ? nextValue - 1 : this.absoluteMax;

    return Math.max(minValue, Math.min(this.absoluteMax, maxValue));
  }

  getInlineInputId(section: ThresholdSectionKey, controlName: ThresholdControlName): string {
    return `${section}-${controlName}-inline-input`;
  }

  getThresholdValidationMessage(form: UntypedFormGroup = this.companyThresholdForm): string {
    if (form.invalid) {
      return 'Preencha as quatro faixas com números entre 1 e 99.';
    }

    const payload = this.getThresholdPayload(form);
    if (!(payload.veryBadMax < payload.needProcessingMax)) {
      return 'A faixa de cuidados urgentes precisa terminar antes da faixa de precisa de cuidados.';
    }

    if (!(payload.needProcessingMax < payload.canImproveMax)) {
      return 'A faixa de precisa de cuidados precisa terminar antes da faixa de pode melhorar.';
    }

    if (!(payload.canImproveMax < payload.veryGoodMax)) {
      return 'A faixa de pode melhorar precisa terminar antes da faixa de muito bom.';
    }

    if (payload.veryGoodMax >= 100) {
      return 'A faixa de muito bom precisa terminar antes de 100 para que a faixa excelente exista.';
    }

    return '';
  }

  isThresholdFormValid(form: UntypedFormGroup = this.companyThresholdForm): boolean {
    return this.getThresholdValidationMessage(form) === '';
  }

  getClassificationPreview(form: UntypedFormGroup): IClassificationPreviewItem[] {
    if (!this.isThresholdFormValid(form)) {
      return [];
    }

    const payload = this.getThresholdPayload(form);

    return [
      {
        label: 'Cuidados urgentes',
        range: `Abaixo de ${payload.veryBadMax}`,
        dotClass: 'preview-dot--urgent'
      },
      {
        label: 'Precisa de cuidados',
        range: `${payload.veryBadMax} até ${payload.needProcessingMax - 1}`,
        dotClass: 'preview-dot--care'
      },
      {
        label: 'Pode melhorar',
        range: `${payload.needProcessingMax} até ${payload.canImproveMax - 1}`,
        dotClass: 'preview-dot--improve'
      },
      {
        label: 'Muito bom',
        range: `${payload.canImproveMax} até ${payload.veryGoodMax - 1}`,
        dotClass: 'preview-dot--good'
      },
      {
        label: 'Excelente',
        range: `${payload.veryGoodMax} ou mais`,
        dotClass: 'preview-dot--excellent'
      }
    ];
  }

  getSelectedCompanyName(): string {
    const selectedCompanyId = String(this.companyControl.value || '');
    if (!selectedCompanyId) {
      return 'Nenhuma empresa selecionada';
    }

    return this.companies.find((company) => String(company.id) === selectedCompanyId)?.name || 'Empresa selecionada';
  }

  private createThresholdForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      veryBadMax: new UntypedFormControl(null, [Validators.required, Validators.min(1), Validators.max(99)]),
      needProcessingMax: new UntypedFormControl(null, [Validators.required, Validators.min(1), Validators.max(99)]),
      canImproveMax: new UntypedFormControl(null, [Validators.required, Validators.min(1), Validators.max(99)]),
      veryGoodMax: new UntypedFormControl(null, [Validators.required, Validators.min(1), Validators.max(99)])
    });
  }

  private applyThresholdSettings(
    form: UntypedFormGroup,
    settings: ISkincareThresholdSettings | IUpsertSkincareThresholdSettingsInput
  ): void {
    form.patchValue(
      {
        veryBadMax: settings.veryBadMax,
        needProcessingMax: settings.needProcessingMax,
        canImproveMax: settings.canImproveMax,
        veryGoodMax: settings.veryGoodMax
      },
      { emitEvent: false }
    );
  }

  private getThresholdPayload(
    form: UntypedFormGroup = this.companyThresholdForm
  ): IUpsertSkincareThresholdSettingsInput {
    const rawValue = form.getRawValue();

    return {
      veryBadMax: Number(rawValue.veryBadMax),
      needProcessingMax: Number(rawValue.needProcessingMax),
      canImproveMax: Number(rawValue.canImproveMax),
      veryGoodMax: Number(rawValue.veryGoodMax)
    };
  }

  private getThresholdForm(section: ThresholdSectionKey): UntypedFormGroup {
    return section === 'default' ? this.defaultThresholdForm : this.companyThresholdForm;
  }

  private getPreviousControlName(controlName: ThresholdControlName): ThresholdControlName | null {
    switch (controlName) {
      case 'needProcessingMax':
        return 'veryBadMax';
      case 'canImproveMax':
        return 'needProcessingMax';
      case 'veryGoodMax':
        return 'canImproveMax';
      default:
        return null;
    }
  }

  private getNextControlName(controlName: ThresholdControlName): ThresholdControlName | null {
    switch (controlName) {
      case 'veryBadMax':
        return 'needProcessingMax';
      case 'needProcessingMax':
        return 'canImproveMax';
      case 'canImproveMax':
        return 'veryGoodMax';
      default:
        return null;
    }
  }
}
